import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDocs, 
  getDoc, 
  query, 
  where, 
  orderBy, 
  limit,
  serverTimestamp,
  Timestamp,
  deleteField,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { Capacitor } from '@capacitor/core';
import { firestoreRestApi } from '../utils/firestoreRestApi';
import { getActivityMiles } from '../utils/activityUtils';
import { DailyActivity, ActivityFormData, ActivitySubmissionResult } from '../types';
import { challengeService } from './challengeService';

class ActivityService {
  private activitiesCollection = collection(db, 'dailyActivities');
  // If Firestore SDK reads are blocked by rules on web, switch to REST-first to avoid noisy repeated errors.
  private preferRestForActivityReads = false;
  private static readonly MAX_MILES_PER_ENTRY = 30;

  // Calculate points based on submission timing (legacy method for activity document storage)
  // New rules: By 8 AM next day = full points (1.0), After 8 AM but before 3 PM = half points (0.5), After 3 PM = no points (0)
  private calculatePoints(submittedAt: Date, targetDate: string): { points: number; timing: 'same_day' | 'early_next' | 'late_next' | 'no_points' } {
    // Parse activity date in local timezone to avoid UTC conversion issues
    const activityDateObj = new Date(targetDate + 'T12:00:00'); // Use noon to avoid timezone issues
    const submissionTime = submittedAt.getHours() * 60 + submittedAt.getMinutes();
    
    // Check if submission is same day
    const isSameDay = submittedAt.toDateString() === activityDateObj.toDateString();
    
    if (isSameDay) {
      // Same day submission - gets full points (before 8 AM next day deadline)
      console.log('Points: 1.0 (same_day)');
      return { points: 1.0, timing: 'same_day' };
    } else {
      // Next day submission
      const nextDay = new Date(activityDateObj);
      nextDay.setDate(nextDay.getDate() + 1);
      
      // Check if submission is next day
      const isNextDay = submittedAt.toDateString() === nextDay.toDateString();
      
      if (isNextDay) {
        // 8 AM = 480 minutes, 3 PM = 900 minutes
        if (submissionTime <= 480) {
          // By 8 AM next day - full points
          console.log('Points: 1.0 (early_next - by 8 AM)');
          return { points: 1.0, timing: 'early_next' };
        } else if (submissionTime <= 900) {
          // After 8 AM but before 3 PM next day - half points
          console.log('Points: 0.5 (late_next - after 8 AM, before 3 PM)');
          return { points: 0.5, timing: 'late_next' };
        } else {
          // After 3 PM next day - no points
          console.log('Points: 0 (no_points - after 3 PM)');
          return { points: 0, timing: 'no_points' };
        }
      } else {
        // More than one day late - no points
        console.log('Points: 0 (no_points - more than one day late)');
        return { points: 0, timing: 'no_points' };
      }
    }
  }

  /** Firestore Timestamp / REST date → milliseconds for sorting. */
  private submittedAtMillis(activity: DailyActivity): number {
    const s = activity.submittedAt as unknown as
      | Timestamp
      | { seconds?: number; _seconds?: number }
      | Date
      | undefined;
    if (!s) return 0;
    if (typeof (s as Timestamp).toDate === 'function') {
      return (s as Timestamp).toDate().getTime();
    }
    const sec = (s as { seconds?: number; _seconds?: number }).seconds
      ?? (s as { _seconds?: number })._seconds;
    if (typeof sec === 'number') return sec * 1000;
    if (s instanceof Date) return s.getTime();
    return 0;
  }

  /**
   * For stats averages: use only the most recent submission that calendar day.
   * A metric at 0 means "not entered" for that log — it is excluded from that
   * metric's average (e.g. Rest Day with no health does not reuse earlier same-day values).
   */
  private latestActivityHealthForDayStats(dayDocs: DailyActivity[]): {
    sleepScore: number | null;
    hydrationLevel: number | null;
    nutritionLevel: number | null;
  } {
    if (!dayDocs.length) {
      return { sleepScore: null, hydrationLevel: null, nutritionLevel: null };
    }
    const sorted = [...dayDocs].sort((a, b) => this.submittedAtMillis(b) - this.submittedAtMillis(a));
    const latest = sorted[0];
    const s = latest.sleepScore ?? 0;
    const h = latest.hydrationLevel ?? 0;
    const n = latest.nutritionLevel ?? 0;
    return {
      sleepScore: s > 0 ? s : null,
      hydrationLevel: h > 0 ? h : null,
      nutritionLevel: n > 0 ? n : null,
    };
  }

  // Check if user has already submitted for a specific date
  async hasSubmittedForDate(userId: string, date: string): Promise<boolean> {
    try {
      const q = query(
        this.activitiesCollection,
        where('userId', '==', userId),
        where('date', '==', date)
      );
      const querySnapshot = await getDocs(q);
      return !querySnapshot.empty;
    } catch (error) {
      console.error('Error checking submission status:', error);
      return false;
    }
  }

  // Get today's date in YYYY-MM-DD format
  getTodayDateString(): string {
    const today = new Date();
    // Use local timezone instead of UTC
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  // Check if it's after 8 PM (should show activity modals)
  shouldShowActivityModals(): boolean {
    // For testing: always return true (remove time requirement)
    return true;
    
    // Original time-based logic (commented out for testing):
    // const now = new Date();
    // const hour = now.getHours();
    // return hour >= 20; // 8 PM or later
  }

  /** Zeros irrelevant fields for the selected runType so CT vs strength minutes stay separate in Firestore. */
  private normalizeActivityFormData(raw: ActivityFormData): ActivityFormData {
    const clampMiles = (value: number | undefined): number => {
      const n = Number.isFinite(value as number) ? (value as number) : 0;
      return Math.min(ActivityService.MAX_MILES_PER_ENTRY, Math.max(0, n));
    };
    const activityData: ActivityFormData = {
      ...raw,
      strengthMinutes: raw.strengthMinutes ?? 0,
      crossTrainingMinutes: raw.crossTrainingMinutes ?? 0,
      milesRun: clampMiles(raw.milesRun),
      warmupMiles: clampMiles(raw.warmupMiles),
      workoutMiles: clampMiles(raw.workoutMiles),
      cooldownMiles: clampMiles(raw.cooldownMiles),
    };
    const rt = activityData.runType;

    if (rt === 'Strength') {
      activityData.crossTrainingMinutes = 0;
      activityData.crossTrainingType = undefined;
      activityData.milesRun = 0;
      activityData.warmupMiles = 0;
      activityData.workoutMiles = 0;
      activityData.cooldownMiles = 0;
      activityData.runSubType = undefined;
      activityData.workoutType = undefined;
    } else if (rt === 'Cross Training') {
      activityData.strengthMinutes = 0;
      activityData.milesRun = 0;
      activityData.warmupMiles = 0;
      activityData.workoutMiles = 0;
      activityData.cooldownMiles = 0;
      activityData.runSubType = undefined;
      activityData.workoutType = undefined;
    } else if (rt === 'Run') {
      activityData.strengthMinutes = 0;
      activityData.crossTrainingMinutes = 0;
      activityData.crossTrainingType = undefined;
      if (activityData.runSubType === 'Workout') {
        activityData.milesRun = 0;
      } else {
        activityData.warmupMiles = 0;
        activityData.workoutMiles = 0;
        activityData.cooldownMiles = 0;
        activityData.workoutType = undefined;
      }
    } else if (rt === 'Workout') {
      activityData.strengthMinutes = 0;
      activityData.crossTrainingMinutes = 0;
      activityData.crossTrainingType = undefined;
      activityData.milesRun = 0;
      activityData.runSubType = undefined;
    } else {
      activityData.strengthMinutes = 0;
      activityData.crossTrainingMinutes = 0;
      activityData.crossTrainingType = undefined;
      activityData.milesRun = 0;
      activityData.warmupMiles = 0;
      activityData.workoutMiles = 0;
      activityData.cooldownMiles = 0;
      activityData.runSubType = undefined;
      activityData.workoutType = undefined;
    }
    return activityData;
  }

  /** Fire-and-forget: match saved activity against team challenges (see challengeActivityMatch). */
  private enqueueChallengeEvaluationFromForm(
    userId: string,
    teamId: string,
    d: ActivityFormData,
    activityDateYmd: string
  ): void {
    const payload = {
      runType: d.runType,
      runSubType: d.runSubType,
      milesRun: d.milesRun,
      warmupMiles: d.warmupMiles,
      workoutMiles: d.workoutMiles,
      cooldownMiles: d.cooldownMiles,
      crossTrainingMinutes: d.crossTrainingMinutes,
      strengthMinutes: d.strengthMinutes ?? 0,
    };
    void challengeService
      .processActivityForChallenges(userId, teamId, payload, activityDateYmd)
      .catch((err) => console.warn('[ActivityService] Challenge evaluation:', err));
  }

  private coerceToActivityFormData(
    activityData: Partial<ActivityFormData> & {
      sleepScore: number;
      hydrationLevel: number;
      nutritionLevel: number;
      runType: ActivityFormData['runType'];
      milesRun: number;
      crossTrainingMinutes: number;
    }
  ): ActivityFormData {
    return this.normalizeActivityFormData({
      sleepScore: activityData.sleepScore ?? 0,
      hydrationLevel: activityData.hydrationLevel ?? 0,
      nutritionLevel: activityData.nutritionLevel ?? 0,
      runType: activityData.runType,
      runSubType: activityData.runSubType,
      workoutType: activityData.workoutType,
      crossTrainingType: activityData.crossTrainingType,
      milesRun: activityData.milesRun ?? 0,
      warmupMiles: activityData.warmupMiles ?? 0,
      workoutMiles: activityData.workoutMiles ?? 0,
      cooldownMiles: activityData.cooldownMiles ?? 0,
      crossTrainingMinutes: activityData.crossTrainingMinutes ?? 0,
      strengthMinutes: activityData.strengthMinutes ?? 0,
    });
  }

  /**
   * Build update payload for daily activity.
   * @param forRestApi When true, omit Firestore deleteField() (REST merge cannot send it); optional fields are left unset instead.
   */
  private buildDailyActivityUpdateData(
    activityData: ActivityFormData,
    useTimestamp: boolean,
    forRestApi: boolean = false
  ): Record<string, any> {
    const d = this.normalizeActivityFormData(activityData);
    const ts = useTimestamp ? serverTimestamp() : new Date();
    const splitMiles = d.runType === 'Workout' || (d.runType === 'Run' && d.runSubType === 'Workout');

    const updateData: any = {
      sleepScore: d.sleepScore,
      hydrationLevel: d.hydrationLevel,
      nutritionLevel: d.nutritionLevel,
      runType: d.runType,
      milesRun: d.milesRun,
      crossTrainingMinutes: d.crossTrainingMinutes,
      strengthMinutes: d.strengthMinutes,
      warmupMiles: d.warmupMiles,
      workoutMiles: d.workoutMiles,
      cooldownMiles: d.cooldownMiles,
      updatedAt: ts,
    };

    if (d.runType === 'Run' && d.runSubType) {
      updateData.runSubType = d.runSubType;
    } else if (!forRestApi) {
      updateData.runSubType = deleteField();
    }

    if (splitMiles && d.workoutType) {
      updateData.workoutType = d.workoutType;
    } else if (!forRestApi) {
      updateData.workoutType = deleteField();
    }

    if (d.runType === 'Cross Training' && d.crossTrainingType) {
      updateData.crossTrainingType = d.crossTrainingType;
    } else if (!forRestApi) {
      updateData.crossTrainingType = deleteField();
    }

    return updateData;
  }

  // Update daily activity
  async updateDailyActivity(
    activityId: string,
    activityData: ActivityFormData
  ): Promise<ActivitySubmissionResult> {
    const notFoundResult: ActivitySubmissionResult = {
      success: false,
      pointsEarned: 0,
      experiencePointsEarned: 0,
      walletPointsEarned: 0,
      streakPointsEarned: 0,
      submissionTiming: 'no_points',
      currentLevel: 0,
      currentStreak: 0,
      message: 'Activity not found.',
    };

    // On iOS, use REST API only. Firestore SDK getDoc/updateDoc can hang and never resolve.
    if (Capacitor.getPlatform() === 'ios') {
      try {
        console.log('[ActivityService] iOS: using REST API for updateDailyActivity');
        const restDoc = await firestoreRestApi.getDocument('dailyActivities', activityId);
        if (!restDoc || !restDoc.data) {
          return notFoundResult;
        }
        const existingData = typeof restDoc.data === 'function' ? restDoc.data() : restDoc;
        const dNorm = this.normalizeActivityFormData(activityData);
        const updateDataRest = this.buildDailyActivityUpdateData(activityData, false, true);
        await firestoreRestApi.setDocument('dailyActivities', activityId, updateDataRest, true);
        this.enqueueChallengeEvaluationFromForm(
          existingData.userId,
          existingData.teamId,
          dNorm,
          existingData.date
        );
        return {
          success: true,
          pointsEarned: existingData.pointsEarned || 0,
          experiencePointsEarned: 0,
          walletPointsEarned: 0,
          streakPointsEarned: 0,
          submissionTiming: existingData.submissionTiming,
          currentLevel: 0,
          currentStreak: 0,
          message: 'Activity updated successfully!',
        };
      } catch (restError: any) {
        console.error('[ActivityService] iOS REST updateDailyActivity failed:', restError);
        return {
          success: false,
          pointsEarned: 0,
          experiencePointsEarned: 0,
          walletPointsEarned: 0,
          streakPointsEarned: 0,
          submissionTiming: 'no_points',
          currentLevel: 0,
          currentStreak: 0,
          message: restError.message || 'Failed to update daily activity.',
        };
      }
    }

    try {
      const activityRef = doc(db, 'dailyActivities', activityId);
      const activityDoc = await getDoc(activityRef);

      if (!activityDoc.exists()) {
        return notFoundResult;
      }

      const existingActivity = activityDoc.data() as DailyActivity;
      const dNorm = this.normalizeActivityFormData(activityData);
      const updateData = this.buildDailyActivityUpdateData(activityData, true, false);
      await updateDoc(activityRef, updateData);

      this.enqueueChallengeEvaluationFromForm(
        existingActivity.userId,
        existingActivity.teamId,
        dNorm,
        existingActivity.date
      );

      return {
        success: true,
        pointsEarned: existingActivity.pointsEarned || 0,
        experiencePointsEarned: 0,
        walletPointsEarned: 0,
        streakPointsEarned: 0,
        submissionTiming: existingActivity.submissionTiming,
        currentLevel: 0,
        currentStreak: 0,
        message: 'Activity updated successfully!',
      };
    } catch (error: any) {
      const isPermissionError =
        error?.code === 'permission-denied' || error?.message?.includes('permission') || error?.message?.includes('insufficient permissions');
      if (isPermissionError) {
        try {
          console.warn('[ActivityService] SDK permission denied; retrying updateDailyActivity via REST API');
          const restDoc = await firestoreRestApi.getDocument('dailyActivities', activityId);
          if (!restDoc || !restDoc.data) {
            return notFoundResult;
          }
          const existingData = typeof restDoc.data === 'function' ? restDoc.data() : restDoc;
          const dNorm = this.normalizeActivityFormData(activityData);
          const updateDataRest = this.buildDailyActivityUpdateData(activityData, false, true);
          await firestoreRestApi.setDocument('dailyActivities', activityId, updateDataRest, true);
          this.enqueueChallengeEvaluationFromForm(
            existingData.userId,
            existingData.teamId,
            dNorm,
            existingData.date
          );
          return {
            success: true,
            pointsEarned: existingData.pointsEarned || 0,
            experiencePointsEarned: 0,
            walletPointsEarned: 0,
            streakPointsEarned: 0,
            submissionTiming: existingData.submissionTiming,
            currentLevel: 0,
            currentStreak: 0,
            message: 'Activity updated successfully!',
          };
        } catch (restError: any) {
          console.error('[ActivityService] REST fallback for updateDailyActivity failed:', restError);
          return {
            success: false,
            pointsEarned: 0,
            experiencePointsEarned: 0,
            walletPointsEarned: 0,
            streakPointsEarned: 0,
            submissionTiming: 'no_points',
            currentLevel: 0,
            currentStreak: 0,
            message: restError.message || 'Failed to update daily activity.',
          };
        }
      }
      console.error('Error updating daily activity:', error);
      return {
        success: false,
        pointsEarned: 0,
        experiencePointsEarned: 0,
        walletPointsEarned: 0,
        streakPointsEarned: 0,
        submissionTiming: 'no_points',
        currentLevel: 0,
        currentStreak: 0,
        message: error.message || 'Failed to update daily activity.',
      };
    }
  }

  // Submit daily activity for a specific date
  async submitDailyActivityForDate(
    userId: string,
    teamId: string,
    dateString: string,
    activityData: ActivityFormData
  ): Promise<ActivitySubmissionResult> {
    try {
      // Validate teamId
      if (!teamId || teamId.trim() === '') {
        return {
          success: false,
          pointsEarned: 0,
          experiencePointsEarned: 0,
          walletPointsEarned: 0,
          streakPointsEarned: 0,
          submissionTiming: 'no_points',
          currentLevel: 0,
          currentStreak: 0,
          message: 'Team ID is required to submit daily activity. Please ensure you are properly associated with a team.'
        };
      }

      // Check if already submitted for this date
      const alreadySubmitted = await this.hasSubmittedForDate(userId, dateString);
      if (alreadySubmitted) {
        return {
          success: false,
          pointsEarned: 0,
          experiencePointsEarned: 0,
          walletPointsEarned: 0,
          streakPointsEarned: 0,
          submissionTiming: 'no_points',
          currentLevel: 0,
          currentStreak: 0,
          message: 'You have already submitted your daily activity for this date.'
        };
      }

      const submittedAt = new Date();
      const activityDate = new Date(dateString); // Date for point calculation

      // Calculate points based on timing
      const { points, timing } = this.calculatePoints(submittedAt, dateString);

      const d = this.normalizeActivityFormData(activityData);

      // Create activity document with only the fields that exist and are not undefined
      const activityDoc: any = {
        userId,
        teamId,
        date: dateString,
        submittedAt: Timestamp.fromDate(submittedAt),
        sleepScore: d.sleepScore,
        hydrationLevel: d.hydrationLevel,
        nutritionLevel: d.nutritionLevel,
        runType: d.runType,
        milesRun: d.milesRun,
        crossTrainingMinutes: d.crossTrainingMinutes,
        strengthMinutes: d.strengthMinutes,
        pointsEarned: points,
        submissionTiming: timing,
      };

      if (d.runSubType !== undefined && d.runSubType !== null) {
        activityDoc.runSubType = d.runSubType;
      }
      if (d.workoutType !== undefined && d.workoutType !== null) {
        activityDoc.workoutType = d.workoutType;
      }
      if (d.crossTrainingType !== undefined && d.crossTrainingType !== null) {
        activityDoc.crossTrainingType = d.crossTrainingType;
      }
      if (d.warmupMiles !== undefined && d.warmupMiles !== null) {
        activityDoc.warmupMiles = d.warmupMiles;
      }
      if (d.workoutMiles !== undefined && d.workoutMiles !== null) {
        activityDoc.workoutMiles = d.workoutMiles;
      }
      if (d.cooldownMiles !== undefined && d.cooldownMiles !== null) {
        activityDoc.cooldownMiles = d.cooldownMiles;
      }

      // Debug: Log the final document before saving
      console.log('Final activityDoc before Firebase:', JSON.stringify(activityDoc, null, 2));
      
      const docRef = await addDoc(this.activitiesCollection, {
        ...activityDoc,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      // Points are now awarded through the new dual points system in submitDailyActivity()
      this.enqueueChallengeEvaluationFromForm(userId, teamId, d, dateString);

      return {
        success: true,
        pointsEarned: points,
        experiencePointsEarned: points, // Legacy points same as XP for this function
        walletPointsEarned: points, // Legacy points same as WP for this function
        streakPointsEarned: 0, // No streak bonus in this function
        submissionTiming: timing,
        currentLevel: 0, // Not calculated in this function
        currentStreak: 0, // Not calculated in this function
        message: this.getSubmissionMessage(timing, points)
      };

    } catch (error: any) {
      console.error('Error submitting daily activity:', error);
      return {
        success: false,
        pointsEarned: 0,
        experiencePointsEarned: 0,
        walletPointsEarned: 0,
        streakPointsEarned: 0,
        submissionTiming: 'no_points',
        currentLevel: 0,
        currentStreak: 0,
        message: error.message || 'Failed to submit daily activity.'
      };
    }
  }

  // Submit health metrics only (for today)
  async submitHealthMetrics(
    userId: string, 
    teamId: string, 
    healthData: {
      sleepScore: number;
      hydrationLevel: number;
      nutritionLevel: number;
    },
    date?: string // Optional date in YYYY-MM-DD format, defaults to today if not provided
  ): Promise<ActivitySubmissionResult> {
    try {
      // Debug: Log the health data to see what's being passed
      console.log('Health data received:', JSON.stringify(healthData, null, 2));
      
      // Validate teamId
      if (!teamId || teamId.trim() === '') {
        return {
          success: false,
          pointsEarned: 0,
          experiencePointsEarned: 0,
          walletPointsEarned: 0,
          streakPointsEarned: 0,
          submissionTiming: 'no_points',
          currentLevel: 0,
          currentStreak: 0,
          message: 'Team ID is required to submit health metrics. Please ensure you are properly associated with a team.'
        };
      }

      // Use provided date or default to today
      const activityDate = date || this.getTodayDateString();
      const submittedAt = new Date();
      
      // Check if already submitted for the selected date
      const existingActivities = await this.getUserActivitiesForDate(userId, activityDate, teamId);
      if (existingActivities.length > 0) {
        // Check if any existing activity has health metrics
        const hasHealthMetrics = existingActivities.some(activity => 
          activity.sleepScore > 0 || activity.hydrationLevel > 0 || activity.nutritionLevel > 0
        );
        
        if (hasHealthMetrics) {
          return {
            success: false,
            pointsEarned: 0,
            experiencePointsEarned: 0,
            walletPointsEarned: 0,
            streakPointsEarned: 0,
            submissionTiming: 'no_points',
            currentLevel: 0,
            currentStreak: 0,
            message: `Health metrics have already been submitted for ${activityDate === this.getTodayDateString() ? 'today' : activityDate}.`
          };
        }
      }

      const dateString = activityDate;

      // Create health metrics only document (no running activity fields)
      const activityDoc: any = {
        userId,
        teamId,
        date: dateString,
        submittedAt: Timestamp.fromDate(submittedAt),
        sleepScore: healthData.sleepScore,
        hydrationLevel: healthData.hydrationLevel,
        nutritionLevel: healthData.nutritionLevel,
      };

      // Debug: Log the final document before saving
      console.log('Final health metrics doc before Firebase:', JSON.stringify(activityDoc, null, 2));
      
      // Add to Firestore
      let docId: string;
      const platform = Capacitor.getPlatform();
      
      if (platform === 'ios') {
        console.log('iOS detected - using REST API for submitHealthMetrics');
        // Use REST API on iOS
        docId = await firestoreRestApi.createDocument('dailyActivities', {
          ...activityDoc,
          createdAt: new Date(),
          updatedAt: new Date(),
        });
        console.log('Health metrics created via REST API with ID:', docId);
      } else {
        // Use SDK on web/Android, but fall back to REST API if SDK auth/rules evaluation fails
        try {
          const docRef = await addDoc(this.activitiesCollection, {
            ...activityDoc,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          });
          docId = docRef.id;
        } catch (createError: any) {
          if (createError?.code === 'permission-denied' || createError?.message?.includes('permission')) {
            console.warn('Permission denied via SDK; retrying submitHealthMetrics create via REST API');
            docId = await firestoreRestApi.createDocument('dailyActivities', {
              ...activityDoc,
              createdAt: new Date(),
              updatedAt: new Date(),
            });
            console.log('Health metrics created via REST fallback with ID:', docId);
          } else {
            throw createError;
          }
        }
      }

      // Award points using the new dual points system
      // Health metrics only: hasHealthMetrics = true, hasActivity = false
      let pointsResult: {
        experiencePoints: number;
        walletPoints: number;
        streakBonus: number;
        timing: string;
        newLevel: number;
        newStreak: number;
      };
      try {
        const { pointsService } = await import('./pointsService');
        pointsResult = await pointsService.awardDailyLoggingPoints(
          userId, 
          teamId, 
          dateString, 
          submittedAt,
          {
            sleepScore: healthData.sleepScore,
            hydrationLevel: healthData.hydrationLevel,
            nutritionLevel: healthData.nutritionLevel,
            runType: 'None', // No activity
            milesRun: 0,
            crossTrainingMinutes: 0,
            strengthMinutes: 0,
          }
        );
      } catch (pointsError) {
        console.error('Health metrics saved, but awarding points failed:', pointsError);
        pointsResult = {
          experiencePoints: 0,
          walletPoints: 0,
          streakBonus: 0,
          timing: 'same_day',
          newLevel: 0,
          newStreak: 0,
        };
      }

      return {
        success: true,
        pointsEarned: pointsResult.walletPoints,
        experiencePointsEarned: pointsResult.experiencePoints,
        walletPointsEarned: pointsResult.walletPoints,
        streakPointsEarned: pointsResult.streakBonus,
        submissionTiming: pointsResult.timing as any,
        currentLevel: pointsResult.newLevel,
        currentStreak: pointsResult.newStreak,
        message: pointsResult.walletPoints > 0
          ? `Health metrics submitted successfully! You earned ${pointsResult.experiencePoints} XP and ${pointsResult.walletPoints} WP (Level ${pointsResult.newLevel}, Streak: ${pointsResult.newStreak} days)`
          : 'Health metrics submitted successfully. No additional points were awarded for this entry type today.'
      };

    } catch (error) {
      console.error('Error submitting health metrics:', error);
      return {
        success: false,
        pointsEarned: 0,
        experiencePointsEarned: 0,
        walletPointsEarned: 0,
        streakPointsEarned: 0,
        submissionTiming: 'no_points',
        currentLevel: 0,
        currentStreak: 0,
        message: 'Failed to submit health metrics. Please try again.'
      };
    }
  }

  // Submit daily activity (for today or specified date)
  async submitDailyActivity(
    userId: string, 
    teamId: string, 
    activityData: Partial<ActivityFormData> & {
      sleepScore: number;
      hydrationLevel: number;
      nutritionLevel: number;
      runType: ActivityFormData['runType'];
      milesRun: number;
      crossTrainingMinutes: number;
    },
    date?: string // Optional date in YYYY-MM-DD format, defaults to today if not provided
  ): Promise<ActivitySubmissionResult> {
    try {
      // Debug: Log the activity data to see what's being passed
      console.log('Activity data received:', JSON.stringify(activityData, null, 2));
      
      // Validate teamId
      if (!teamId || teamId.trim() === '') {
        return {
          success: false,
          pointsEarned: 0,
          experiencePointsEarned: 0,
          walletPointsEarned: 0,
          streakPointsEarned: 0,
          submissionTiming: 'no_points',
          currentLevel: 0,
          currentStreak: 0,
          message: 'Team ID is required to submit daily activity. Please ensure you are properly associated with a team.'
        };
      }

      // Use provided date or default to today
      const activityDate = date || this.getTodayDateString();
      const submittedAt = new Date();
      
      // Note: Removed the limit check to allow multiple running activity logs per day
      // Users can now log multiple running activities on the same day

      const d = this.coerceToActivityFormData(activityData);

      const activityDoc: any = {
        userId,
        teamId,
        date: activityDate,
        submittedAt: Timestamp.fromDate(submittedAt),
        sleepScore: d.sleepScore,
        hydrationLevel: d.hydrationLevel,
        nutritionLevel: d.nutritionLevel,
        runType: d.runType,
        milesRun: d.milesRun,
        crossTrainingMinutes: d.crossTrainingMinutes,
        strengthMinutes: d.strengthMinutes,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      if (d.runSubType !== undefined && d.runSubType !== null) {
        activityDoc.runSubType = d.runSubType;
      }
      if (d.workoutType !== undefined && d.workoutType !== null) {
        activityDoc.workoutType = d.workoutType;
      }
      if (d.crossTrainingType !== undefined && d.crossTrainingType !== null) {
        activityDoc.crossTrainingType = d.crossTrainingType;
      }
      if (d.warmupMiles !== undefined && d.warmupMiles !== null) {
        activityDoc.warmupMiles = d.warmupMiles;
      }
      if (d.workoutMiles !== undefined && d.workoutMiles !== null) {
        activityDoc.workoutMiles = d.workoutMiles;
      }
      if (d.cooldownMiles !== undefined && d.cooldownMiles !== null) {
        activityDoc.cooldownMiles = d.cooldownMiles;
      }

      // Add to Firestore
      let docId: string;
      const platform = Capacitor.getPlatform();
      
      if (platform === 'ios') {
        console.log('iOS detected - using REST API for submitDailyActivity');
        // Use REST API on iOS
        docId = await firestoreRestApi.createDocument('dailyActivities', activityDoc);
        console.log('Activity created via REST API with ID:', docId);
      } else {
        // Use SDK on web/Android, but fall back to REST API if SDK auth/rules evaluation fails
        try {
          const docRef = await addDoc(this.activitiesCollection, activityDoc);
          docId = docRef.id;
        } catch (createError: any) {
          if (createError?.code === 'permission-denied' || createError?.message?.includes('permission')) {
            console.warn('Permission denied via SDK; retrying submitDailyActivity create via REST API');
            docId = await firestoreRestApi.createDocument('dailyActivities', {
              ...activityDoc,
              createdAt: new Date(),
              updatedAt: new Date(),
            });
            console.log('Activity created via REST fallback with ID:', docId);
          } else {
            throw createError;
          }
        }
      }

      // Award points using the new dual points system
      // Pass activity data to calculate health XP and activity XP separately
      let pointsResult: {
        experiencePoints: number;
        walletPoints: number;
        streakBonus: number;
        timing: string;
        newLevel: number;
        newStreak: number;
      };
      try {
        const { pointsService } = await import('./pointsService');
        pointsResult = await pointsService.awardDailyLoggingPoints(
          userId, 
          teamId, 
          activityDate, 
          new Date(),
          {
            sleepScore: d.sleepScore,
            hydrationLevel: d.hydrationLevel,
            nutritionLevel: d.nutritionLevel,
            runType: d.runType,
            milesRun: d.milesRun,
            crossTrainingMinutes: d.crossTrainingMinutes,
            strengthMinutes: d.strengthMinutes,
          }
        );
      } catch (pointsError) {
        console.error('Activity saved, but awarding points failed:', pointsError);
        pointsResult = {
          experiencePoints: 0,
          walletPoints: 0,
          streakBonus: 0,
          timing: 'same_day',
          newLevel: 0,
          newStreak: 0,
        };
      }

      this.enqueueChallengeEvaluationFromForm(userId, teamId, d, activityDate);

      return {
        success: true,
        pointsEarned: pointsResult.walletPoints,
        experiencePointsEarned: pointsResult.experiencePoints,
        walletPointsEarned: pointsResult.walletPoints,
        streakPointsEarned: pointsResult.streakBonus,
        submissionTiming: pointsResult.timing as any,
        currentLevel: pointsResult.newLevel,
        currentStreak: pointsResult.newStreak,
        message: pointsResult.walletPoints <= 0
          ? 'Activity logged successfully. No additional points were awarded for this entry type today.'
          : pointsResult.timing === 'no_points' 
          ? 'Activity logged successfully, but no points earned due to late submission.'
          : `Activity logged successfully! You earned ${pointsResult.experiencePoints} XP and ${pointsResult.walletPoints} WP (Level ${pointsResult.newLevel}, Streak: ${pointsResult.newStreak} days)`
      };

    } catch (error) {
      console.error('Error submitting daily activity:', error);
      return {
        success: false,
        pointsEarned: 0,
        experiencePointsEarned: 0,
        walletPointsEarned: 0,
        streakPointsEarned: 0,
        submissionTiming: 'no_points',
        currentLevel: 0,
        currentStreak: 0,
        message: 'Failed to submit daily activity. Please try again.'
      };
    }
  }

  // Get submission message based on timing
  private getSubmissionMessage(timing: string, points: number): string {
    switch (timing) {
      case 'same_day':
        return `Great job! You earned ${points} point for submitting on the same day.`;
      case 'early_next':
        return `Good timing! You earned ${points} points for submitting by 8:00 AM the next day.`;
      case 'late_next':
        return `You earned ${points} points for submitting after 8:00 AM but before 3:00 PM the next day.`;
      case 'no_points':
        return 'Activity logged successfully. No points earned (submitted after 3:00 PM the next day).';
      default:
        return 'Activity logged successfully.';
    }
  }

  // Get user's activity history
  async getUserActivities(userId: string, limitCount: number = 30, teamId?: string): Promise<DailyActivity[]> {
    try {
      // On iOS, use REST API workaround.
      // On web/Android, once SDK permission-denied is detected, use REST-first.
      if (Capacitor.getPlatform() === 'ios' || this.preferRestForActivityReads) {
        if (Capacitor.getPlatform() === 'ios') {
          console.log('Using Firestore REST API for getUserActivities on iOS');
        }
        const filters: Array<{ field: string; op: '=='; value: string }> = [
          { field: 'userId', op: '==', value: userId }
        ];
        if (teamId) {
          filters.push({ field: 'teamId', op: '==', value: teamId });
        }
        const documents = await firestoreRestApi.queryDocuments(
          'dailyActivities',
          filters,
          limitCount,
          { fieldPath: 'date', direction: 'DESCENDING' }
        );
        
        const activities = documents.map(doc => ({
          id: doc.id,
          ...doc.data()
        } as DailyActivity));
        
        activities.sort((a, b) => {
          const d = (b.date || '').localeCompare(a.date || '');
          if (d !== 0) return d;
          return this.submittedAtMillis(b) - this.submittedAtMillis(a);
        });
        
        return activities;
      }
      
      // On web/Android, use SDK
      const q = teamId
        ? query(
            this.activitiesCollection,
            where('userId', '==', userId),
            where('teamId', '==', teamId),
            orderBy('date', 'desc'),
            limit(limitCount)
          )
        : query(
            this.activitiesCollection,
            where('userId', '==', userId),
            orderBy('date', 'desc'),
            limit(limitCount)
          );
      
      const querySnapshot = await getDocs(q);
      const activities = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as DailyActivity));
      
      activities.sort((a, b) => {
        const d = (b.date || '').localeCompare(a.date || '');
        if (d !== 0) return d;
        return this.submittedAtMillis(b) - this.submittedAtMillis(a);
      });
      
      return activities;
    } catch (error: any) {
      // If it's a permission error, try REST API fallback (similar to getTeamActivities)
      if (error?.code === 'permission-denied' || error?.message?.includes('permission')) {
        // Promote to REST-first to avoid repeated SDK permission errors on every screen refresh.
        this.preferRestForActivityReads = true;
        console.warn('Permission denied via SDK for getUserActivities; switching to REST-first for activity reads.');
        
        // Try REST API fallback for athletes/coaches who can't read via SDK
        try {
          console.warn('Retrying getUserActivities via REST API');
          const filters: Array<{ field: string; op: '=='; value: string }> = [
            { field: 'userId', op: '==', value: userId }
          ];
          if (teamId) {
            filters.push({ field: 'teamId', op: '==', value: teamId });
          }
          const documents = await firestoreRestApi.queryDocuments(
            'dailyActivities',
            filters,
            limitCount,
            { fieldPath: 'date', direction: 'DESCENDING' }
          );
          
          const activities = documents.map(doc => ({
            id: doc.id,
            ...doc.data()
          } as DailyActivity));
          
          activities.sort((a, b) => {
            const d = (b.date || '').localeCompare(a.date || '');
            if (d !== 0) return d;
            return this.submittedAtMillis(b) - this.submittedAtMillis(a);
          });
          console.log(`REST fallback succeeded: loaded ${activities.length} user activities`);
          return activities;
        } catch (restError: any) {
          console.error('REST fallback failed for getUserActivities:', {
            message: restError?.message,
            code: restError?.code,
            userId,
            teamId: teamId || ''
          });
        }
      } else {
        console.error('Error fetching user activities:', error);
        console.error('Error details:', {
          message: error?.message,
          code: error?.code,
          userId,
          teamId: teamId || '',
          platform: Capacitor.getPlatform()
        });
      }
      
      return [];
    }
  }

  /**
   * Activities for one user on one team between startDate and endDate (YYYY-MM-DD), sorted by date ascending.
   * Used for coach athlete progression (Race Day logs in a season window).
   */
  async getUserActivitiesInDateRange(
    userId: string,
    teamId: string,
    startDate: string,
    endDate: string,
    maxDocs: number = 2000
  ): Promise<DailyActivity[]> {
    const runRest = async (): Promise<DailyActivity[]> => {
      const filters: Array<{ field: string; op: string; value: string }> = [
        { field: 'userId', op: '==', value: userId },
        { field: 'teamId', op: '==', value: teamId },
        { field: 'date', op: 'GREATER_THAN_OR_EQUAL', value: startDate },
        { field: 'date', op: 'LESS_THAN_OR_EQUAL', value: endDate },
      ];
      const documents = await firestoreRestApi.queryDocuments('dailyActivities', filters, maxDocs);
      const activities = documents.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as DailyActivity[];
      activities.sort((a, b) => a.date.localeCompare(b.date));
      return activities;
    };

    try {
      if (Capacitor.getPlatform() === 'ios' || this.preferRestForActivityReads) {
        return await runRest();
      }
      const q = query(
        this.activitiesCollection,
        where('userId', '==', userId),
        where('teamId', '==', teamId),
        where('date', '>=', startDate),
        where('date', '<=', endDate),
        orderBy('date', 'asc'),
        limit(maxDocs)
      );
      const querySnapshot = await getDocs(q);
      const activities = querySnapshot.docs.map(
        (d) => ({ id: d.id, ...d.data() } as DailyActivity)
      );
      return activities;
    } catch (error: any) {
      if (error?.code === 'permission-denied' || error?.message?.includes('permission')) {
        this.preferRestForActivityReads = true;
        try {
          return await runRest();
        } catch (restErr) {
          console.error('getUserActivitiesInDateRange REST failed:', restErr);
        }
      } else {
        console.error('getUserActivitiesInDateRange:', error);
      }
      return [];
    }
  }

  /**
   * Progression chart: merge team-scoped activities with any same-user activities in the date window.
   * Coach bulk race logs sometimes land on docs with missing/wrong teamId; those are invisible to
   * getUserActivitiesInDateRange(teamId) alone.
   */
  async getUserActivitiesForProgression(
    userId: string,
    teamId: string,
    startDate: string,
    endDate: string,
    maxDocs: number = 2000
  ): Promise<DailyActivity[]> {
    const byTeam = await this.getUserActivitiesInDateRange(
      userId,
      teamId,
      startDate,
      endDate,
      maxDocs
    );

    const runUserDateRest = async (): Promise<DailyActivity[]> => {
      const filters: Array<{ field: string; op: string; value: string }> = [
        { field: 'userId', op: '==', value: userId },
        { field: 'date', op: 'GREATER_THAN_OR_EQUAL', value: startDate },
        { field: 'date', op: 'LESS_THAN_OR_EQUAL', value: endDate },
      ];
      const documents = await firestoreRestApi.queryDocuments('dailyActivities', filters, maxDocs);
      const activities = documents.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as DailyActivity[];
      activities.sort((a, b) => a.date.localeCompare(b.date));
      return activities;
    };

    let byUserRange: DailyActivity[] = [];
    try {
      if (Capacitor.getPlatform() === 'ios' || this.preferRestForActivityReads) {
        byUserRange = await runUserDateRest();
      } else {
        const q = query(
          this.activitiesCollection,
          where('userId', '==', userId),
          where('date', '>=', startDate),
          where('date', '<=', endDate),
          orderBy('date', 'asc'),
          limit(maxDocs)
        );
        const snap = await getDocs(q);
        byUserRange = snap.docs.map((d) => ({ id: d.id, ...d.data() } as DailyActivity));
      }
    } catch (e: any) {
      if (e?.code === 'permission-denied' || e?.message?.includes('permission')) {
        this.preferRestForActivityReads = true;
        try {
          byUserRange = await runUserDateRest();
        } catch {
          const recent = await this.getUserActivities(userId, Math.min(maxDocs, 500), undefined);
          byUserRange = recent.filter((a) => a.date >= startDate && a.date <= endDate);
        }
      } else if (e?.code === 'failed-precondition' || e?.message?.includes('index')) {
        try {
          byUserRange = await runUserDateRest();
        } catch {
          const recent = await this.getUserActivities(userId, Math.min(maxDocs, 500), undefined);
          byUserRange = recent.filter((a) => a.date >= startDate && a.date <= endDate);
        }
      } else {
        console.warn('[ActivityService] getUserActivitiesForProgression user+date query failed:', e);
        const recent = await this.getUserActivities(userId, Math.min(maxDocs, 500), undefined);
        byUserRange = recent.filter((a) => a.date >= startDate && a.date <= endDate);
      }
    }

    const map = new Map<string, DailyActivity>();
    for (const a of byTeam) map.set(a.id, a);
    for (const a of byUserRange) {
      if (!map.has(a.id)) map.set(a.id, a);
    }
    return Array.from(map.values()).sort((a, b) => a.date.localeCompare(b.date));
  }

  /**
   * Team leaderboard: merge team-scoped docs with each athlete's full activity list.
   * Coach bulk race saves sometimes write dailyActivities without teamId; those are invisible to
   * getTeamActivities alone but match what Athlete progression shows.
   */
  async mergeTeamActivitiesWithUserActivities(
    userId: string,
    teamActivitiesCache: DailyActivity[]
  ): Promise<DailyActivity[]> {
    const fromTeam = teamActivitiesCache.filter((a) => a.userId === userId);
    let fromUser: DailyActivity[] = [];
    try {
      fromUser = await this.getUserActivities(userId, 2000, undefined);
    } catch (e) {
      console.warn('[ActivityService] mergeTeamActivitiesWithUserActivities:', e);
    }
    const map = new Map<string, DailyActivity>();
    for (const a of fromTeam) {
      if (a.id) map.set(a.id, a);
    }
    for (const a of fromUser) {
      if (a.id) map.set(a.id, a);
    }
    return Array.from(map.values());
  }

  // Get team's activity history
  async getTeamActivities(teamId: string, limitCount: number = 100): Promise<DailyActivity[]> {
    try {
      // Remove orderBy to avoid permission issues with complex rules
      // Sort client-side instead
      const q = query(
        this.activitiesCollection,
        where('teamId', '==', teamId),
        limit(limitCount)
      );
      
      const querySnapshot = await getDocs(q);
      const activities = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as DailyActivity));
      
      // Sort by date descending (most recent first) on client side
      activities.sort((a, b) => b.date.localeCompare(a.date));
      
      return activities;
    } catch (error: any) {
      console.error('Error fetching team activities:', error);
      console.error('Error details:', {
        message: error?.message,
        code: error?.code,
        teamId,
        platform: Capacitor.getPlatform()
      });

      // Web auth can be present in app state while SDK auth is stale.
      // Retry via REST API so analytics still loads for head coach users.
      if (error?.code === 'permission-denied' || error?.message?.includes('permission')) {
        try {
          console.warn('Permission denied via SDK; retrying getTeamActivities via REST API');
          const documents = await firestoreRestApi.queryDocuments(
            'dailyActivities',
            [{ field: 'teamId', op: '==', value: teamId }],
            limitCount
          );

          const activities = documents.map(doc => ({
            id: doc.id,
            ...doc.data()
          } as DailyActivity));

          activities.sort((a, b) => b.date.localeCompare(a.date));
          console.log(`REST fallback succeeded: loaded ${activities.length} team activities`);
          return activities;
        } catch (restError: any) {
          console.error('REST fallback failed for getTeamActivities:', {
            message: restError?.message,
            code: restError?.code,
            teamId
          });
        }
      }

      return [];
    }
  }

  // Get activity by ID
  async getActivityById(activityId: string): Promise<DailyActivity | null> {
    try {
      const docRef = doc(this.activitiesCollection, activityId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return {
          id: docSnap.id,
          ...docSnap.data()
        } as DailyActivity;
      }
      return null;
    } catch (error) {
      console.error('Error fetching activity:', error);
      return null;
    }
  }

  private isActivityPermissionError(error: any): boolean {
    return (
      error?.code === 'permission-denied' ||
      (typeof error?.message === 'string' &&
        (error.message.includes('permission') || error.message.includes('insufficient permissions')))
    );
  }

  // Update activity
  async updateActivity(activityId: string, updates: Partial<ActivityFormData>): Promise<boolean> {
    try {
      const docRef = doc(this.activitiesCollection, activityId);
      await updateDoc(docRef, {
        ...updates,
        updatedAt: Timestamp.now()
      });
      return true;
    } catch (error: any) {
      if (this.isActivityPermissionError(error)) {
        try {
          console.warn('[ActivityService] updateActivity SDK denied; retrying via REST merge');
          await this.patchDailyActivityFields(activityId, { ...updates });
          return true;
        } catch (restErr) {
          console.error('[ActivityService] REST updateActivity failed:', restErr);
        }
      }
      console.error('Error updating activity:', error);
      return false;
    }
  }

  /**
   * Merge fields into a daily activity (coach bulk race entry, etc.).
   * Uses REST PATCH if the SDK hits permission-denied (same rules apply, but avoids some SDK quirks).
   */
  async patchDailyActivityFields(activityId: string, fields: Record<string, unknown>): Promise<void> {
    const updatePayload: Record<string, unknown> = { ...fields, updatedAt: Timestamp.now() };
    try {
      await updateDoc(doc(this.activitiesCollection, activityId), updatePayload as any);
      return;
    } catch (error: any) {
      if (!this.isActivityPermissionError(error)) {
        throw error;
      }
      console.warn('[ActivityService] patchDailyActivityFields SDK denied; REST merge');
      const rest: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(updatePayload)) {
        if (v === undefined || v === null) continue;
        if (k === 'id') continue;
        rest[k] =
          v instanceof Timestamp
            ? v.toDate()
            : v && typeof v === 'object' && 'toDate' in (v as object) && typeof (v as any).toDate === 'function'
              ? (v as any).toDate()
              : v;
      }
      await firestoreRestApi.setDocument('dailyActivities', activityId, rest, true);
    }
  }

  // Delete activity
  async deleteActivity(activityId: string): Promise<boolean> {
    try {
      const docRef = doc(this.activitiesCollection, activityId);
      await deleteDoc(docRef);
      return true;
    } catch (error: any) {
      const isPermissionError =
        error?.code === 'permission-denied' || error?.message?.includes('permission') || error?.message?.includes('insufficient permissions');
      if (isPermissionError) {
        try {
          console.warn('[ActivityService] SDK permission denied; retrying deleteActivity via REST API');
          await firestoreRestApi.deleteDocument('dailyActivities', activityId);
          return true;
        } catch (restError: any) {
          console.error('[ActivityService] REST fallback for deleteActivity failed:', restError);
          return false;
        }
      }
      console.error('Error deleting activity:', error);
      return false;
    }
  }

  // Get activity statistics: averages use the `limitCount` most recent calendar dates.
  // Sleep / hydration / nutrition: only the latest submission per day counts; values of 0
  // are excluded from that metric's average (no entry for that metric on that day).
  async getUserActivityStats(userId: string, limitCount: number = 7, teamId?: string): Promise<{
    totalSubmissions: number;
    averageSleepScore: number;
    averageHydrationLevel: number;
    averageNutritionLevel: number;
    totalMilesRun: number;
    totalPointsEarned: number;
    submissionTimingBreakdown: {
      same_day: number;
      early_next: number;
      late_next: number;
      no_points: number;
    };
  }> {
    try {
      const bufferSize = Math.max(200, limitCount * 25);
      const activities = await this.getUserActivities(userId, bufferSize, teamId);
      
      const stats = {
        totalSubmissions: 0,
        averageSleepScore: 0,
        averageHydrationLevel: 0,
        averageNutritionLevel: 0,
        totalMilesRun: 0,
        totalPointsEarned: 0,
        submissionTimingBreakdown: {
          same_day: 0,
          early_next: 0,
          late_next: 0,
          no_points: 0
        }
      };

      if (activities.length === 0) return stats;

      const distinctDatesDesc: string[] = [];
      for (const a of activities) {
        if (!a.date) continue;
        if (!distinctDatesDesc.includes(a.date)) distinctDatesDesc.push(a.date);
        if (distinctDatesDesc.length >= limitCount) break;
      }

      // Full per-day fetches so "latest submission that day" uses every log for that date,
      // not a random subset from the date-ordered buffer (which caused low/wrong averages).
      const dayDocsPerDate = await Promise.all(
        distinctDatesDesc.map(date => this.getUserActivitiesForDate(userId, date, teamId))
      );

      const dayRows = dayDocsPerDate.map(docs => this.latestActivityHealthForDayStats(docs));
      const relevantActivities = dayDocsPerDate.flat();

      stats.totalSubmissions = relevantActivities.length;

      relevantActivities.forEach(activity => {
        stats.totalMilesRun += getActivityMiles(activity);
        stats.totalPointsEarned += activity.pointsEarned || 0;
        stats.submissionTimingBreakdown[activity.submissionTiming]++;
      });

      const sleepSamples = dayRows.map(r => r.sleepScore).filter((v): v is number => v !== null);
      const hydrationSamples = dayRows.map(r => r.hydrationLevel).filter((v): v is number => v !== null);
      const nutritionSamples = dayRows.map(r => r.nutritionLevel).filter((v): v is number => v !== null);

      stats.averageSleepScore =
        sleepSamples.length > 0
          ? Math.round((sleepSamples.reduce((a, b) => a + b, 0) / sleepSamples.length) * 10) / 10
          : 0;
      stats.averageHydrationLevel =
        hydrationSamples.length > 0
          ? Math.round((hydrationSamples.reduce((a, b) => a + b, 0) / hydrationSamples.length) * 10) / 10
          : 0;
      stats.averageNutritionLevel =
        nutritionSamples.length > 0
          ? Math.round((nutritionSamples.reduce((a, b) => a + b, 0) / nutritionSamples.length) * 10) / 10
          : 0;

      return stats;
    } catch (error) {
      console.error('Error calculating activity stats:', error);
      return {
        totalSubmissions: 0,
        averageSleepScore: 0,
        averageHydrationLevel: 0,
        averageNutritionLevel: 0,
        totalMilesRun: 0,
        totalPointsEarned: 0,
        submissionTimingBreakdown: {
          same_day: 0,
          early_next: 0,
          late_next: 0,
          no_points: 0
        }
      };
    }
  }

  // Get activities for a specific date
  async getUserActivitiesForDate(userId: string, date: string, teamId?: string): Promise<DailyActivity[]> {
    try {
      // On iOS, use REST API workaround.
      // On web/Android, once SDK permission-denied is detected, use REST-first.
      if (Capacitor.getPlatform() === 'ios' || this.preferRestForActivityReads) {
        const filters: Array<{ field: string; op: '=='; value: string }> = [
          { field: 'userId', op: '==', value: userId },
          { field: 'date', op: '==', value: date }
        ];
        if (teamId) {
          filters.push({ field: 'teamId', op: '==', value: teamId });
        }
        const documents = await firestoreRestApi.queryDocuments('dailyActivities', filters);

        const activities = documents.map(doc => ({
          id: doc.id,
          ...doc.data() as Omit<DailyActivity, 'id'>
        }));
        activities.sort((a, b) => this.submittedAtMillis(b) - this.submittedAtMillis(a));
        return activities;
      }
      
      // On web/Android, use SDK — equality only (no orderBy on submittedAt) so we do not
      // require a userId+teamId+date+submittedAt composite index; Firestore may otherwise
      // return an arbitrary order and stats would pick the wrong "latest" row.
      const activitiesQuery = teamId
        ? query(
            this.activitiesCollection,
            where('userId', '==', userId),
            where('teamId', '==', teamId),
            where('date', '==', date)
          )
        : query(
            this.activitiesCollection,
            where('userId', '==', userId),
            where('date', '==', date)
          );
      
      const snapshot = await getDocs(activitiesQuery);
      const activities = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data() as Omit<DailyActivity, 'id'>
      }));
      activities.sort((a, b) => this.submittedAtMillis(b) - this.submittedAtMillis(a));
      return activities;
    } catch (error: any) {
      // SDK query can fail due to auth/rules evaluation quirks; retry via REST API
      if (error?.code === 'permission-denied' || error?.message?.includes('permission')) {
        this.preferRestForActivityReads = true;
        try {
          console.warn('Permission denied via SDK for getUserActivitiesForDate; using REST API');
          const filters: Array<{ field: string; op: '=='; value: string }> = [
            { field: 'userId', op: '==', value: userId },
            { field: 'date', op: '==', value: date }
          ];
          if (teamId) {
            filters.push({ field: 'teamId', op: '==', value: teamId });
          }
          const documents = await firestoreRestApi.queryDocuments('dailyActivities', filters);

          const activities = documents.map(doc => ({
            id: doc.id,
            ...doc.data() as Omit<DailyActivity, 'id'>
          }));
          activities.sort((a, b) => this.submittedAtMillis(b) - this.submittedAtMillis(a));
          console.log(`REST fallback succeeded: loaded ${activities.length} activities for date ${date}`);
          return activities;
        } catch (restError) {
          console.error('REST fallback failed for getUserActivitiesForDate:', restError);
        }
      } else {
        console.error('Error getting activities for date:', error);
      }

      return [];
    }
  }

  // Get athlete's health metrics history (last N days)
  async getAthleteHealthMetricsHistory(athleteId: string, limitCount: number = 30, teamId?: string): Promise<DailyActivity[]> {
    try {
      // If teamId is provided, use getTeamActivities to avoid permission issues for head coaches
      // This allows head coaches to fetch activities without querying by userId
      if (teamId) {
        console.log(`getAthleteHealthMetricsHistory: Using team-based query for athlete ${athleteId} in team ${teamId}`);
        const teamActivities = await this.getTeamActivities(teamId, 1000);
        // Filter by athleteId, deduplicate by id and date+userId, and sort by date descending
        const seenIds = new Set<string>();
        const seenDateKeys = new Set<string>();
        const filteredActivities = teamActivities
          .filter(activity => {
            // Filter by athleteId
            if (activity.userId !== athleteId) return false;
            
            // Deduplicate by activity id
            if (seenIds.has(activity.id)) return false;
            seenIds.add(activity.id);
            
            // Also deduplicate by date+userId to ensure only one entry per date
            const dateKey = `${activity.date}_${activity.userId}`;
            if (seenDateKeys.has(dateKey)) {
              console.log(`getAthleteHealthMetricsHistory: Skipping duplicate date entry: ${dateKey}`);
              return false;
            }
            seenDateKeys.add(dateKey);
            
            return true;
          })
          .sort((a, b) => b.date.localeCompare(a.date))
          .slice(0, limitCount);
        console.log(`getAthleteHealthMetricsHistory: Found ${filteredActivities.length} unique activities for athlete ${athleteId}`);
        return filteredActivities;
      }
      
      // Otherwise, use the direct query (works for regular coaches and athletes)
      const activitiesQuery = query(
        this.activitiesCollection,
        where('userId', '==', athleteId),
        orderBy('date', 'desc'),
        limit(limitCount)
      );
      
      const snapshot = await getDocs(activitiesQuery);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data() as Omit<DailyActivity, 'id'>
      }));
    } catch (error) {
      console.error('Error getting athlete health metrics history:', error);
      return [];
    }
  }

  // Update health metrics by coach with audit trail
  async updateHealthMetricsByCoach(
    activityId: string,
    coachId: string,
    healthData: {
      sleepScore: number;
      hydrationLevel: number;
      nutritionLevel: number;
    },
    notes?: string
  ): Promise<{ success: boolean; message: string }> {
    // On iOS, always use REST API workaround
    if (Capacitor.getPlatform() === 'ios') {
      console.log('[ActivityService] iOS detected - using REST API for updateHealthMetricsByCoach');
      return await this.updateHealthMetricsByCoachViaRest(activityId, coachId, healthData, notes);
    }

    // On web/Android, use SDK with REST fallback
    try {
      const activityRef = doc(this.activitiesCollection, activityId);
      const activitySnap = await getDoc(activityRef);
      
      if (!activitySnap.exists()) {
        return { success: false, message: 'Activity not found' };
      }

      const currentActivity = activitySnap.data() as DailyActivity;
      
      // Build modification history entry
      const changes: any = {};
      if (currentActivity.sleepScore !== healthData.sleepScore) {
        changes.sleepScore = { from: currentActivity.sleepScore, to: healthData.sleepScore };
      }
      if (currentActivity.hydrationLevel !== healthData.hydrationLevel) {
        changes.hydrationLevel = { from: currentActivity.hydrationLevel, to: healthData.hydrationLevel };
      }
      if (currentActivity.nutritionLevel !== healthData.nutritionLevel) {
        changes.nutritionLevel = { from: currentActivity.nutritionLevel, to: healthData.nutritionLevel };
      }

      // Create history entry (only include notes if provided)
      const historyEntry: any = {
        timestamp: Timestamp.now(),
        modifiedBy: coachId,
        modifiedByRole: 'coach' as const,
        changes
      };
      
      if (notes && notes.trim()) {
        historyEntry.notes = notes.trim();
      }

      // Prepare update data
      const updateData: any = {
        sleepScore: healthData.sleepScore,
        hydrationLevel: healthData.hydrationLevel,
        nutritionLevel: healthData.nutritionLevel,
        lastModifiedBy: coachId,
        lastModifiedByRole: 'coach',
        modificationHistory: [
          ...(currentActivity.modificationHistory || []),
          historyEntry
        ],
        updatedAt: serverTimestamp()
      };

      if (notes && notes.trim()) {
        updateData.coachNotes = notes.trim();
      }

      await updateDoc(activityRef, updateData);
      
      return { success: true, message: 'Health metrics updated successfully' };
    } catch (error: any) {
      if (error?.code === 'permission-denied' || error?.message?.includes('permission')) {
        console.warn('[ActivityService] SDK permission denied; retrying updateHealthMetricsByCoach via REST API');
        return await this.updateHealthMetricsByCoachViaRest(activityId, coachId, healthData, notes);
      }
      console.error('Error updating health metrics by coach:', error);
      return { success: false, message: 'Failed to update health metrics' };
    }
  }

  private async updateHealthMetricsByCoachViaRest(
    activityId: string,
    coachId: string,
    healthData: {
      sleepScore: number;
      hydrationLevel: number;
      nutritionLevel: number;
    },
    notes?: string
  ): Promise<{ success: boolean; message: string }> {
    try {
      // Get current activity via REST
      const activityDoc = await firestoreRestApi.getDocument('dailyActivities', activityId);
      if (!activityDoc) {
        return { success: false, message: 'Activity not found' };
      }

      const currentActivity = activityDoc.data() as DailyActivity;
      
      // Build modification history entry
      const changes: any = {};
      if (currentActivity.sleepScore !== healthData.sleepScore) {
        changes.sleepScore = { from: currentActivity.sleepScore, to: healthData.sleepScore };
      }
      if (currentActivity.hydrationLevel !== healthData.hydrationLevel) {
        changes.hydrationLevel = { from: currentActivity.hydrationLevel, to: healthData.hydrationLevel };
      }
      if (currentActivity.nutritionLevel !== healthData.nutritionLevel) {
        changes.nutritionLevel = { from: currentActivity.nutritionLevel, to: healthData.nutritionLevel };
      }

      // Create history entry (only include notes if provided)
      const historyEntry: any = {
        timestamp: new Date(),
        modifiedBy: coachId,
        modifiedByRole: 'coach' as const,
        changes
      };
      
      if (notes && notes.trim()) {
        historyEntry.notes = notes.trim();
      }

      // Prepare update data
      const updateData: any = {
        sleepScore: healthData.sleepScore,
        hydrationLevel: healthData.hydrationLevel,
        nutritionLevel: healthData.nutritionLevel,
        lastModifiedBy: coachId,
        lastModifiedByRole: 'coach',
        modificationHistory: [
          ...(currentActivity.modificationHistory || []),
          historyEntry
        ],
        updatedAt: new Date()
      };

      if (notes && notes.trim()) {
        updateData.coachNotes = notes.trim();
      }

      // Update via REST API (merge mode to preserve other fields)
      await firestoreRestApi.setDocument('dailyActivities', activityId, updateData, true);
      
      return { success: true, message: 'Health metrics updated successfully' };
    } catch (error: any) {
      console.error('Error updating health metrics via REST API:', error);
      return { success: false, message: 'Failed to update health metrics' };
    }
  }

  // Create health metrics for athlete by coach
  async createHealthMetricsForAthlete(
    athleteId: string,
    teamId: string,
    coachId: string,
    date: string,
    healthData: {
      sleepScore: number;
      hydrationLevel: number;
      nutritionLevel: number;
    },
    notes?: string
  ): Promise<{ success: boolean; message: string }> {
    // On iOS, always use REST API workaround
    if (Capacitor.getPlatform() === 'ios') {
      console.log('[ActivityService] iOS detected - using REST API for createHealthMetricsForAthlete');
      return await this.createHealthMetricsForAthleteViaRest(athleteId, teamId, coachId, date, healthData, notes);
    }

    // On web/Android, use SDK with REST fallback
    try {
      // Check if activity already exists for this date
      const existing = await this.getUserActivitiesForDate(athleteId, date, teamId);
      if (existing.length > 0) {
        return { 
          success: false, 
          message: 'Activity already exists for this date. Please use edit instead.' 
        };
      }

      const submittedAt = new Date();
      
      // Calculate points (coach entries still get points based on timing)
      const { points, timing } = this.calculatePoints(submittedAt, date);

      // Create history entry (only include notes if provided)
      const historyEntry: any = {
        timestamp: Timestamp.now(),
        modifiedBy: coachId,
        modifiedByRole: 'coach' as const,
        changes: {
          sleepScore: { from: 0, to: healthData.sleepScore },
          hydrationLevel: { from: 0, to: healthData.hydrationLevel },
          nutritionLevel: { from: 0, to: healthData.nutritionLevel }
        }
      };
      
      if (notes && notes.trim()) {
        historyEntry.notes = notes.trim();
      } else {
        historyEntry.notes = 'Created by coach';
      }

      // Create activity document
      const activityDoc: any = {
        userId: athleteId,
        teamId,
        date,
        submittedAt: Timestamp.fromDate(submittedAt),
        sleepScore: healthData.sleepScore,
        hydrationLevel: healthData.hydrationLevel,
        nutritionLevel: healthData.nutritionLevel,
        pointsEarned: points,
        submissionTiming: timing,
        lastModifiedBy: coachId,
        lastModifiedByRole: 'coach',
        modificationHistory: [historyEntry],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      if (notes && notes.trim()) {
        activityDoc.coachNotes = notes.trim();
      }

      await addDoc(this.activitiesCollection, activityDoc);
      
      return { 
        success: true, 
        message: `Health metrics created successfully. ${points} point${points !== 1 ? 's' : ''} awarded.` 
      };
    } catch (error: any) {
      if (error?.code === 'permission-denied' || error?.message?.includes('permission')) {
        console.warn('[ActivityService] SDK permission denied; retrying createHealthMetricsForAthlete via REST API');
        return await this.createHealthMetricsForAthleteViaRest(athleteId, teamId, coachId, date, healthData, notes);
      }
      console.error('Error creating health metrics for athlete:', error);
      return { success: false, message: 'Failed to create health metrics' };
    }
  }

  private async createHealthMetricsForAthleteViaRest(
    athleteId: string,
    teamId: string,
    coachId: string,
    date: string,
    healthData: {
      sleepScore: number;
      hydrationLevel: number;
      nutritionLevel: number;
    },
    notes?: string
  ): Promise<{ success: boolean; message: string }> {
    try {
      // Check if activity already exists for this date
      const existing = await this.getUserActivitiesForDate(athleteId, date, teamId);
      if (existing.length > 0) {
        return { 
          success: false, 
          message: 'Activity already exists for this date. Please use edit instead.' 
        };
      }

      const submittedAt = new Date();
      
      // Calculate points (coach entries still get points based on timing)
      const { points, timing } = this.calculatePoints(submittedAt, date);

      // Create history entry (only include notes if provided)
      const historyEntry: any = {
        timestamp: new Date(),
        modifiedBy: coachId,
        modifiedByRole: 'coach' as const,
        changes: {
          sleepScore: { from: 0, to: healthData.sleepScore },
          hydrationLevel: { from: 0, to: healthData.hydrationLevel },
          nutritionLevel: { from: 0, to: healthData.nutritionLevel }
        }
      };
      
      if (notes && notes.trim()) {
        historyEntry.notes = notes.trim();
      } else {
        historyEntry.notes = 'Created by coach';
      }

      // Create activity document
      const activityDoc: any = {
        userId: athleteId,
        teamId,
        date,
        submittedAt: submittedAt,
        sleepScore: healthData.sleepScore,
        hydrationLevel: healthData.hydrationLevel,
        nutritionLevel: healthData.nutritionLevel,
        pointsEarned: points,
        submissionTiming: timing,
        lastModifiedBy: coachId,
        lastModifiedByRole: 'coach',
        modificationHistory: [historyEntry],
        createdAt: new Date(),
        updatedAt: new Date()
      };

      if (notes && notes.trim()) {
        activityDoc.coachNotes = notes.trim();
      }

      await firestoreRestApi.createDocument('dailyActivities', activityDoc);
      
      return { 
        success: true, 
        message: `Health metrics created successfully. ${points} point${points !== 1 ? 's' : ''} awarded.` 
      };
    } catch (error: any) {
      console.error('Error creating health metrics via REST API:', error);
      return { success: false, message: 'Failed to create health metrics' };
    }
  }
}

export const activityService = new ActivityService();
